// Financial Reporting tab — issue a report request.
//
// The server owns what a type asks for (the code-resident catalog), the
// suggested period, the computed due date, and whether the type may be issued
// at all. This dialog owns exactly one thing: which of those items B3 actually
// wants this time (decision 13), plus one optional off-catalog extra.
//
// Every default item arrives TICKED — including the "commonly unticked" ones —
// so unticking is a deliberate act rather than something B3 has to remember an
// item exists in order to do. `fanOutCount` is rendered ("×3") because a
// per-guarantor item silently becoming three rows is the kind of multiplication
// that should never be a surprise; a fan-out of 0 is called out too, since the
// server generates nothing at all for it.

import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import {
  Button,
  Card,
  Input,
  Skeleton,
  StatusBadge,
  Textarea,
  useToast,
} from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { extractErrorMessage } from "@/api/client";
import { calendarDateInputToIso, formatCalendarDate } from "@/design-system/lib/format";
import {
  REPORT_REQUEST_ITEM_KIND_LABEL,
  REPORT_REQUEST_ITEM_KINDS,
  REPORT_REQUEST_TYPES,
  REPORT_REQUEST_TYPE_LABEL,
  type ReportRequestItemKind,
  type ReportRequestType,
} from "@/api/portfolioAdmin/reportRequestsApi";
import {
  useIssueReportRequest,
  useReportRequestIssueOptions,
} from "@/hooks/portfolioAdmin/useReportRequests";

interface IssueReportRequestModalProps {
  loanId: string;
  open: boolean;
  onClose: () => void;
  /** Pre-selected type — the DSCR-Watch panel opens straight into the
   *  monthly type rather than making B3 find it. */
  initialType?: ReportRequestType;
}

function toDateInputValue(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

export function IssueReportRequestModal({
  loanId,
  open,
  onClose,
  initialType = "Quarterly",
}: IssueReportRequestModalProps) {
  const toast = useToast();
  const [type, setType] = useState<ReportRequestType>(initialType);
  const [selected, setSelected] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState("");
  const [adHocName, setAdHocName] = useState("");
  const [adHocKind, setAdHocKind] = useState<ReportRequestItemKind>("Upload");
  const [adHocGuidance, setAdHocGuidance] = useState("");

  const options = useReportRequestIssueOptions(loanId, type, open);
  const issue = useIssueReportRequest(loanId);

  // Re-seed to the caller's type each time the dialog opens, so opening it
  // from the DSCR-Watch panel always lands on the monthly type even if the
  // last visit left it elsewhere.
  useEffect(() => {
    if (open) setType(initialType);
  }, [open, initialType]);

  // Tick the defaults and take the server's due date whenever a fresh set of
  // options arrives (dialog opened, or type switched).
  const data = options.data;
  useEffect(() => {
    if (!data) return;
    setSelected(data.items.filter((i) => i.selectedByDefault).map((i) => i.itemCode));
    setDueDate(toDateInputValue(data.suggestedDueDate));
  }, [data]);

  // Reset the ad-hoc extra when the dialog closes, so a half-typed one from
  // last time never rides along on the next request.
  useEffect(() => {
    if (open) return;
    setAdHocName("");
    setAdHocGuidance("");
    setAdHocKind("Upload");
  }, [open]);

  const toggle = (code: string) =>
    setSelected((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );

  const adHocTrimmed = adHocName.trim();
  const canSubmit =
    !!data &&
    data.canIssue &&
    dueDate !== "" &&
    (selected.length > 0 || adHocTrimmed.length > 0) &&
    !issue.isPending;

  const submit = () => {
    if (!data) return;
    const iso = calendarDateInputToIso(dueDate);
    if (!iso) {
      toast.error("Enter a valid due date.");
      return;
    }
    issue.mutate(
      {
        requestType: type,
        periodStart: data.suggestedPeriodStart,
        periodEnd: data.suggestedPeriodEnd,
        periodLabel: data.suggestedPeriodLabel,
        dueDate: iso,
        itemCodes: selected,
        adHocItemName: adHocTrimmed || null,
        adHocItemKind: adHocTrimmed ? adHocKind : null,
        adHocItemGuidance: adHocTrimmed ? adHocGuidance.trim() || null : null,
      },
      {
        onSuccess: () => {
          toast.success("Report request sent to the borrower.");
          onClose();
        },
        onError: (err) => toast.error(extractErrorMessage(err)),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Issue a report request"
      description="The borrower receives one bundled request in their portal inbox."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={issue.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={issue.isPending}
            disabled={!canSubmit}
            onClick={submit}
            data-testid="issue-request-submit"
          >
            Send to borrower
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-4">
        {/* Type picker */}
        <div>
          <span className="block text-small font-medium text-onyx mb-space-2">
            Request type
          </span>
          <div className="flex flex-wrap gap-space-2" role="group" aria-label="Request type">
            {REPORT_REQUEST_TYPES.map((t) => (
              <button
                key={t}
                type="button"
                aria-pressed={type === t}
                onClick={() => setType(t)}
                data-testid={`issue-type-${t}`}
                className={
                  type === t
                    ? "rounded-full border border-gold bg-gold/15 px-space-3 py-space-1 text-small font-medium text-onyx"
                    : "rounded-full border border-border-light bg-white-pure px-space-3 py-space-1 text-small text-gray-mid hover:border-gold"
                }
              >
                {REPORT_REQUEST_TYPE_LABEL[t]}
              </button>
            ))}
          </div>
        </div>

        {options.isLoading && (
          <div className="flex flex-col gap-space-2" data-testid="issue-options-loading">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        )}

        {options.isError && (
          <p className="text-small text-status-error m-0" role="alert">
            Could not load what this request type asks for.
          </p>
        )}

        {data && (
          <>
            {/* Blocked types say why, and submit stays disabled. */}
            {!data.canIssue && (
              <div
                className="flex items-start gap-space-2 rounded-md border border-status-warning-ink bg-status-warning/15 px-space-3 py-space-2"
                data-testid="issue-blocked-reason"
                role="alert"
              >
                <AlertTriangle
                  size={16}
                  aria-hidden
                  className="mt-[2px] shrink-0 text-status-warning-ink"
                />
                <p className="text-small text-status-warning-ink m-0">
                  {data.blockedReason ??
                    "This request type cannot be issued for this loan right now."}
                </p>
              </div>
            )}

            {/* Period (server-suggested) + due date (editable) */}
            <div className="flex flex-wrap items-end gap-space-4">
              <div>
                <span className="block text-small font-medium text-onyx mb-space-1">
                  Reporting period
                </span>
                <p className="text-body text-onyx m-0" data-testid="issue-period">
                  {data.suggestedPeriodLabel}
                </p>
                <p className="text-caption text-gray-mid m-0">
                  {formatCalendarDate(data.suggestedPeriodStart)} –{" "}
                  {formatCalendarDate(data.suggestedPeriodEnd)}
                </p>
              </div>
              <div className="min-w-[12rem]">
                <label
                  className="block text-small font-medium text-onyx mb-space-1"
                  htmlFor="issue-due-date"
                >
                  Due date
                </label>
                <Input
                  id="issue-due-date"
                  type="date"
                  value={dueDate}
                  disabled={!data.canIssue}
                  onChange={(e) => setDueDate(e.target.value)}
                  data-testid="issue-due-date"
                />
              </div>
            </div>

            {/* Item list — everything ticked, everything untickable */}
            <div>
              <div className="flex items-baseline justify-between gap-space-2">
                <span className="block text-small font-medium text-onyx">
                  What to ask for
                </span>
                <span className="text-caption text-gray-mid" data-testid="issue-selected-count">
                  {selected.length} of {data.items.length} selected
                </span>
              </div>
              <ul className="mt-space-2 flex flex-col gap-space-2 list-none p-0 m-0">
                {data.items.map((item) => {
                  const checked = selected.includes(item.itemCode);
                  const emptyFanOut = item.fanOutCount === 0;
                  return (
                    <li key={item.itemCode}>
                      <label
                        className="flex items-start gap-space-2 rounded-md border border-border-light bg-white-pure px-space-3 py-space-2 cursor-pointer"
                        data-testid={`issue-item-${item.itemCode}`}
                      >
                        <input
                          type="checkbox"
                          className="mt-[3px] shrink-0"
                          checked={checked}
                          onChange={() => toggle(item.itemCode)}
                          aria-label={item.displayName}
                        />
                        <span className="min-w-0">
                          <span className="flex flex-wrap items-center gap-space-2">
                            <span className="text-small font-medium text-onyx">
                              {item.displayName}
                            </span>
                            <StatusBadge tone="neutral">
                              {REPORT_REQUEST_ITEM_KIND_LABEL[item.kind] ?? item.kind}
                            </StatusBadge>
                            {item.applicability === "PerGuarantor" && (
                              <StatusBadge
                                tone={emptyFanOut ? "warn" : "info"}
                                data-testid={`issue-fanout-${item.itemCode}`}
                              >
                                {emptyFanOut
                                  ? "No guarantors — nothing will be created"
                                  : `Per guarantor ×${item.fanOutCount}`}
                              </StatusBadge>
                            )}
                            {item.applicability === "IfApplicable" && (
                              <StatusBadge tone="muted">Often not applicable</StatusBadge>
                            )}
                          </span>
                          <span className="block text-caption text-gray-mid mt-space-1">
                            {item.guidance}
                          </span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* One optional off-catalog extra */}
            <Card>
              <span className="block text-small font-medium text-onyx">
                Ask for one more thing{" "}
                <span className="text-gray-mid font-normal">(optional)</span>
              </span>
              <p className="text-caption text-gray-mid m-0 mt-space-1">
                A one-off item beyond the catalog, for this request only.
              </p>
              <div className="mt-space-2 flex flex-col gap-space-2">
                <div className="flex flex-wrap gap-space-2">
                  <div className="min-w-[14rem] grow">
                    <label
                      className="block text-caption text-gray-mid mb-space-1"
                      htmlFor="ad-hoc-name"
                    >
                      What to ask for
                    </label>
                    <Input
                      id="ad-hoc-name"
                      value={adHocName}
                      onChange={(e) => setAdHocName(e.target.value)}
                      placeholder="e.g. Signed lease amendment"
                      data-testid="ad-hoc-name"
                    />
                  </div>
                  <div className="min-w-[11rem]">
                    <label
                      className="block text-caption text-gray-mid mb-space-1"
                      htmlFor="ad-hoc-kind"
                    >
                      How they provide it
                    </label>
                    <select
                      id="ad-hoc-kind"
                      value={adHocKind}
                      onChange={(e) => setAdHocKind(e.target.value as ReportRequestItemKind)}
                      className="w-full h-10 px-space-3 rounded border border-border-light bg-white-pure text-small text-onyx"
                      data-testid="ad-hoc-kind"
                    >
                      {REPORT_REQUEST_ITEM_KINDS.map((k) => (
                        <option key={k} value={k}>
                          {REPORT_REQUEST_ITEM_KIND_LABEL[k]}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label
                    className="block text-caption text-gray-mid mb-space-1"
                    htmlFor="ad-hoc-guidance"
                  >
                    Instruction for the borrower
                  </label>
                  <Textarea
                    id="ad-hoc-guidance"
                    value={adHocGuidance}
                    onChange={(e) => setAdHocGuidance(e.target.value)}
                    rows={2}
                    placeholder="What exactly they should send, and for what period…"
                    data-testid="ad-hoc-guidance"
                  />
                </div>
              </div>
            </Card>
          </>
        )}
      </div>
    </Modal>
  );
}
